/**
 * usePermissions Hook
 *
 * Central hook for accessing RBAC permissions throughout the application.
 * Provides helpers for checking access levels and getting current permissions.
 *
 * Usage:
 *   const { hasAccess, getCurrentPermissions, permissionState } = usePermissions()
 *
 *   // Check if user can view billing
 *   if (hasAccess('billing', 'viewer')) { ... }
 *
 *   // Check if user can modify inventory
 *   if (hasAccess('inventory', 'operator')) { ... }
 *
 *   // Get all current permissions
 *   const perms = getCurrentPermissions()
 */

import { useCallback, useMemo } from 'react'
import { globalState } from '../store'
import {
  BusinessSegment,
  FunctionRole,
  PermissionState,
  EMPTY_PERMISSIONS,
  LEVEL_HIERARCHY,
  RBACPermissionsResponse,
  transformRBACResponse,
} from '../types/rbac'

interface UsePermissionsReturn {
  /**
   * Check if the current user has at least the specified access level for a segment
   * @param segment The business segment to check (e.g., 'billing', 'inventory')
   * @param minLevel The minimum required access level (default: 'viewer')
   * @returns true if user has sufficient access
   */
  hasAccess: (segment: BusinessSegment, minLevel?: FunctionRole) => boolean

  /**
   * Check if the current user has at least the specified access level at the GLOBAL scope.
   * Unlike hasAccess(), this ignores warehouse-scoped permissions and only checks global.
   * For merchant users, this is equivalent to hasAccess() since merchants have no scope hierarchy.
   *
   * Use this for operations that require global-level authority (e.g., role definition CRUD).
   *
   * @param segment The business segment to check (e.g., 'settings')
   * @param minLevel The minimum required access level (default: 'viewer')
   * @returns true if user has sufficient global access
   */
  hasGlobalAccess: (segment: BusinessSegment, minLevel?: FunctionRole) => boolean

  /**
   * Get the current effective permissions based on active warehouse/company context
   * @returns Record mapping each segment to its access level
   */
  getCurrentPermissions: () => Record<BusinessSegment, FunctionRole>

  /**
   * Get the user's access level for a specific segment
   * @param segment The business segment to check
   * @returns The user's access level for that segment
   */
  getAccessLevel: (segment: BusinessSegment) => FunctionRole

  /**
   * The raw permission state from the store
   */
  permissionState: PermissionState

  /**
   * Whether the user is a warehouse user
   */
  isWarehouseUser: boolean

  /**
   * Whether the user is a merchant user
   */
  isMerchantUser: boolean

  /**
   * The current user's role name (if any)
   */
  currentRoleName: string | null
}

/**
 * Get current effective permissions based on state and active context
 */
function getCurrentPermissionsFromState(
  state: PermissionState,
  activeWarehouseId: number | null
): Record<BusinessSegment, FunctionRole> {
  if (!state) {
    return { ...EMPTY_PERMISSIONS }
  }

  if (state.userType === 'merchant') {
    return state.permissions ?? { ...EMPTY_PERMISSIONS }
  }

  // Warehouse user - check scoped then global, then fallback to empty
  if (activeWarehouseId && state.warehousePermissions[activeWarehouseId.toString()]) {
    return state.warehousePermissions[activeWarehouseId.toString()]
  }

  return state.globalPermissions ?? { ...EMPTY_PERMISSIONS }
}

/**
 * Main hook for accessing RBAC permissions
 */
export function usePermissions(): UsePermissionsReturn {
  const { state } = globalState()

  // Extract RBAC state from store
  // The rbac field comes from the session endpoint
  const rbacResponse = state.rbac as RBACPermissionsResponse | undefined

  // Transform backend response to frontend state
  const permissionState = useMemo<PermissionState>(() => {
    if (!rbacResponse) {
      return null
    }
    return transformRBACResponse(rbacResponse)
  }, [rbacResponse])

  // Get active warehouse from store (for warehouse users)
  const activeWarehouseId = useMemo(() => {
    if (state.warehouseActive && typeof state.warehouseActive === 'object') {
      return (state.warehouseActive as { id?: number }).id ?? null
    }
    return null
  }, [state.warehouseActive])

  // Update activeWarehouseId in permission state when it changes
  const effectivePermissionState = useMemo<PermissionState>(() => {
    if (!permissionState) return null
    if (permissionState.userType === 'warehouse') {
      return {
        ...permissionState,
        activeWarehouseId,
      }
    }
    return permissionState
  }, [permissionState, activeWarehouseId])

  // Get current permissions based on context
  const getCurrentPermissions = useCallback((): Record<BusinessSegment, FunctionRole> => {
    return getCurrentPermissionsFromState(effectivePermissionState, activeWarehouseId)
  }, [effectivePermissionState, activeWarehouseId])

  // Check access for a segment
  const hasAccess = useCallback(
    (segment: BusinessSegment, minLevel: FunctionRole = 'viewer'): boolean => {
      const perms = getCurrentPermissionsFromState(effectivePermissionState, activeWarehouseId)
      const actualLevel = perms[segment] ?? 'none'
      return LEVEL_HIERARCHY[actualLevel] >= LEVEL_HIERARCHY[minLevel]
    },
    [effectivePermissionState, activeWarehouseId]
  )

  // Check global access for a segment (ignores warehouse-scoped permissions)
  const hasGlobalAccess = useCallback(
    (segment: BusinessSegment, minLevel: FunctionRole = 'viewer'): boolean => {
      if (!effectivePermissionState) return false

      // For merchant users, global access = their only permissions
      if (effectivePermissionState.userType === 'merchant') {
        const perms = effectivePermissionState.permissions ?? { ...EMPTY_PERMISSIONS }
        const actualLevel = perms[segment] ?? 'none'
        return LEVEL_HIERARCHY[actualLevel] >= LEVEL_HIERARCHY[minLevel]
      }

      // For warehouse users, check globalPermissions directly (not warehouse-scoped)
      const globalPerms = effectivePermissionState.globalPermissions ?? { ...EMPTY_PERMISSIONS }
      const actualLevel = globalPerms[segment] ?? 'none'
      return LEVEL_HIERARCHY[actualLevel] >= LEVEL_HIERARCHY[minLevel]
    },
    [effectivePermissionState]
  )

  // Get access level for a segment
  const getAccessLevel = useCallback(
    (segment: BusinessSegment): FunctionRole => {
      const perms = getCurrentPermissionsFromState(effectivePermissionState, activeWarehouseId)
      return perms[segment] ?? 'none'
    },
    [effectivePermissionState, activeWarehouseId]
  )

  // Derived state
  const isWarehouseUser = effectivePermissionState?.userType === 'warehouse'
  const isMerchantUser = effectivePermissionState?.userType === 'merchant'

  const currentRoleName = useMemo(() => {
    if (!effectivePermissionState) return null

    if (effectivePermissionState.userType === 'merchant') {
      return effectivePermissionState.role?.name ?? null
    }

    // Warehouse user - get role for current context
    if (activeWarehouseId && effectivePermissionState.roles[activeWarehouseId.toString()]) {
      return effectivePermissionState.roles[activeWarehouseId.toString()]?.name ?? null
    }

    return effectivePermissionState.roles['global']?.name ?? null
  }, [effectivePermissionState, activeWarehouseId])

  return {
    hasAccess,
    hasGlobalAccess,
    getCurrentPermissions,
    getAccessLevel,
    permissionState: effectivePermissionState,
    isWarehouseUser,
    isMerchantUser,
    currentRoleName,
  }
}

/**
 * Standalone function to check access (for use outside React components)
 * Note: This requires the RBAC response and active warehouse ID to be passed in
 */
export function checkAccess(
  rbacResponse: RBACPermissionsResponse | undefined,
  segment: BusinessSegment,
  minLevel: FunctionRole,
  activeWarehouseId: number | null
): boolean {
  if (!rbacResponse) return false

  const permissionState = transformRBACResponse(rbacResponse)
  const perms = getCurrentPermissionsFromState(permissionState, activeWarehouseId)
  const actualLevel = perms[segment] ?? 'none'

  return LEVEL_HIERARCHY[actualLevel] >= LEVEL_HIERARCHY[minLevel]
}

export default usePermissions
